import React, { useState } from 'react';
import { Moon, Sunrise } from 'lucide-react';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

// Un cycle de sommeil dure environ 90 minutes ; se réveiller entre deux cycles
// complets (plutôt qu'en plein sommeil profond) rend généralement le réveil plus
// facile. On ajoute ~15 minutes d'endormissement avant le premier cycle.
const CYCLE_MIN = 90;
const FALL_ASLEEP_MIN = 15;

const parseTime = (str) => {
  if (!str) return null;
  const [h, m] = str.split(':').map(Number);
  return h * 60 + m;
};

const formatTime = (totalMin) => {
  const m = ((totalMin % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, '0')}h${String(min).padStart(2, '0')}`;
};

const SleepCycleCalculator = () => {
  const { t } = useLanguage();
  const [mode, setMode] = useState('wake');
  const [time, setTime] = useState('');

  const parsedTime = parseTime(time);

  let options = [];
  if (parsedTime !== null && mode === 'wake') {
    // Heures de coucher possibles pour se réveiller à l'heure choisie.
    options = [6, 5, 4, 3].map((n) => ({
      cycles: n,
      time: formatTime(parsedTime - FALL_ASLEEP_MIN - n * CYCLE_MIN),
      duration: ((n * CYCLE_MIN) / 60).toFixed(1),
    }));
  } else if (parsedTime !== null && mode === 'sleep') {
    // Heures de réveil possibles en s'endormant à l'heure choisie.
    options = [4, 5, 6].map((n) => ({
      cycles: n,
      time: formatTime(parsedTime + FALL_ASLEEP_MIN + n * CYCLE_MIN),
      duration: ((n * CYCLE_MIN) / 60).toFixed(1),
    }));
  }

  return (
    <ToolPageLayout
      title={t('sleep_title')}
      subtitle={t('sleep_subtitle')}
      specialty="generaliste"
    >
      <div className="flex gap-3 mb-6">
        <button
          type="button"
          onClick={() => setMode('wake')}
          className={`flex-1 rounded-xl px-4 py-3 text-sm font-medium border transition-colors ${mode === 'wake' ? 'bg-[#0596DE] text-white border-[#0596DE]' : 'bg-white text-[#0F172A] border-[#E2E8F0]'}`}
        >
          <Sunrise className="w-4 h-4 inline mr-2" /> {t('sleep_mode_wake')}
        </button>
        <button
          type="button"
          onClick={() => setMode('sleep')}
          className={`flex-1 rounded-xl px-4 py-3 text-sm font-medium border transition-colors ${mode === 'sleep' ? 'bg-[#0596DE] text-white border-[#0596DE]' : 'bg-white text-[#0F172A] border-[#E2E8F0]'}`}
        >
          <Moon className="w-4 h-4 inline mr-2" /> {t('sleep_mode_sleep')}
        </button>
      </div>

      <div className="mb-8">
        <Label className="mb-1 block">{mode === 'wake' ? t('sleep_wake_time_label') : t('sleep_bed_time_label')}</Label>
        <input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-11 max-w-xs focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
        />
      </div>

      {options.length > 0 && (
        <div>
          <h3 className="font-semibold text-[#0F172A] mb-3">
            {mode === 'wake' ? t('sleep_recommended_bedtimes') : t('sleep_recommended_wake')}
          </h3>
          <div className="space-y-2">
            {options.map((o, i) => (
              <div key={i} className={`flex justify-between items-center rounded-xl p-4 border ${i === 1 ? 'bg-[#F0FDF4] border-[#10B981]/30' : 'bg-white border-[#E2E8F0]'}`}>
                <div>
                  <div className="font-semibold text-[#0F172A]">{o.time}</div>
                  <div className="text-xs text-[#64748B]">{o.cycles} {t('sleep_cycles_word')} · {t('sleep_about')} {o.duration} {t('sleep_hours_sleep')}</div>
                </div>
                {i === 1 && <span className="text-xs font-medium text-[#10B981] bg-[#10B981]/10 px-2 py-1 rounded-full">{t('sleep_recommended_badge')}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mt-6 text-sm text-[#64748B]">
        {t('sleep_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default SleepCycleCalculator;
